import { useEffect, useRef } from 'react';
import { request } from '../api/client';
import { trackPixel } from './metaPixel';
import { ROUTE_PATTERNS } from './routePatterns';
import { AD_DEMOS } from '../journey/demos';
import { isTestVisit } from './testVisit';

/**
 * The journey's step events: one per screen reached, so the owner can see where people drop
 * between an ad and a live site. docs/funnel-events.md says what each one means and how to
 * count them.
 *
 * Each step goes to two places, under two different rules:
 *
 *   - Our own ingest, POST /events (infra/lambda/handlers/events.ts), whatever the consent
 *     banner says. It is the first-party product analytics the privacy policy already
 *     describes: an event name, a time, and a few enumerated props — never the idea, a name,
 *     an email or any other words somebody typed. The account rides only the way every API
 *     call carries it, the bearer token the server already reads; no device id is minted for
 *     this and no header is added (a header the API's preflight does not name stops the
 *     request in the browser — see infra/lambda/shared/response.ts).
 *   - Meta, as a custom event of the same name, through `trackPixel` — which sends nothing
 *     without a yes on the banner, and nothing outside a production build.
 *
 * Note for whoever reads the numbers: the ingest keeps only the event names it knows
 * (KNOWN_EVENTS in the handler), and these are new. Until that list names them, the server
 * answers 200 and stores none of them. docs/funnel-events.md has the one-line change.
 */
export const FUNNEL_STEPS = [
  /*
   * Sign-up comes first in the funnel (an ad lands on it), so it is measured first. Names only:
   * never the email, the password or the idea someone arrived with. `signup_submitted` and
   * `code_confirmed` fire on success — an account created and its code accepted — so a refusal
   * stays a drop between steps rather than a step reached.
   */
  'signup_viewed',
  'signup_submitted',
  'code_viewed',
  'code_confirmed',
  'signin_viewed',
  // Left the sign-up page without creating the account (pagehide), and how long they had been on it.
  'signup_left',
  /*
   * Every route, not only the named journey screens (owner, 2026-10-03): the ads link straight here,
   * so this is the only place ad traffic is counted first-party. The route PATTERN only — never an
   * id, a query or the idea — where they came from, and the ad that brought them (analytics/visits.ts).
   */
  'page_viewed',
  'page_left',
  /*
   * The wait for a plan (2026-10-04): pressed, on screen (how long), given up on while it loaded
   * (pagehide, keepalive), or failed (cap, error, timeout). Nothing recorded a press that gave up.
   */
  'plan_requested',
  'plan_ready',
  'plan_abandoned',
  'plan_failed',
  /*
   * The preview-first journey (2026-10-04, landing_pages/docs/preview-first-journey-handoff.md v2):
   * the ad's demo, the composer, Create; then capabilities, looks, the build, the paywall and the
   * reveal; the "still waiting" email; and every message the visitor is shown, by where and a code.
   */
  'preview_viewed',
  'preview_choice',
  'composer_typing_started',
  'composer_attach',
  'create_pressed',
  'capabilities_viewed',
  'capability_toggled',
  'look_viewed',
  'look_chosen',
  'build_abandoned',
  'paywall_left',
  'site_revealed',
  'waiting_email_sent',
  'waiting_email_clicked',
  'error_shown',
  'idea_viewed',
  'plan_viewed',
  'step_features_viewed',
  'step_content_viewed',
  'step_look_viewed',
  'step_review_viewed',
  'build_pressed',
  'paywall_viewed',
  'checkout_started',
  'checkout_returned',
  'build_started',
  'build_succeeded',
  'build_failed',
  'site_opened',
  // Left an app's own browser for Safari or Chrome from the sign-up screen (handoff §4).
  'open_in_browser',
  // How long the ad's live demo took to appear (screens/new/Idea.tsx).
  'preview_loaded',
  // The "Build your own" card that came up over the ad's demo (2026-10-08 to 10-12; kept for the old rows).
  'demo_overlay_shown',
  // trial14-and-demo handoff (2026-10-12): the demo's "Make one like this for you", from the first
  // second; the starting points behind "Start from scratch"; and why someone left the offer.
  'demo_cta_shown',
  'demo_cta_pressed',
  'starter_chosen',
  'offer_exit_reason',
  // The trial offer shown while the app is still being built behind it (Plan.tsx).
  'offer_viewed_during_build',
  // Their own site, shown signed in before the offer comes over it (screens/new/Building.tsx).
  'site_preview_viewed',
  // "Email me my app link" (Building.tsx): asked for, and the email's link opened (any browser).
  'email_captured',
  'email_link_opened',
  // Sign-up's ways in (easy-signup, 2026-10-04): which were offered, which was chosen, and how it ended.
  'signup_method_chosen',
  'signup_failed',
  'signup_completed',
  'signin_completed',
] as const;
export type FunnelStep = (typeof FUNNEL_STEPS)[number];

/**
 * The only props a step may carry, each a closed set of values. A value outside its set is
 * dropped rather than sent, so a caller cannot leak a sentence through a prop by mistake.
 */
const PROP_VALUES = {
  offer: ['trial', 'subscribe'],
  via: ['shortcut', 'review', 'panel'],
  outcome: ['success', 'cancelled'],
  // signup_viewed: whether Build sent them (the other way in is Sign up / Sign in on the shell).
  from_build: [true, false],
  seconds_on_page: ['<3', '3-10', '10-30', '30+'],
  // page_viewed / page_left (analytics/visits.ts).
  path: ROUTE_PATTERNS,
  ref: ['ad', 'landing', 'app', 'direct', 'other'],
  has_fbclid: [true, false],
  // page_left uses the first five; the plan's wait the last four.
  seconds: ['<3', '3-10', '10-30', '30-120', '120+', '<5', '5-10', '10-20', '20+'],
  auto: [true, false],
  // plan_failed's; and offer_exit_reason's answer to "What stopped you?" (handoff §3).
  reason: ['cap', 'error', 'timeout', 'price', 'card', 'not_sure', 'just_looking', 'other'],
  test: [true],
  demo: AD_DEMOS.map((d) => d.slug),
  choice: ['make_own', 'scratch', 'left'],
  prefilled: [true, false],
  seconds_to_first_key: ['<3', '3-10', '10-30', '30+'],
  kind: ['photo', 'document'],
  count: ['1', '2-5', '6+'],
  origin: ['prefilled_unchanged', 'prefilled_edited', 'typed'],
  length: ['<40', '40-120', '120-300', '300+'],
  on: [true, false],
  index: ['0', '1', '2', '3', '4', '5', '6', '7'],
  // build_succeeded: built before the card and held for the trial.
  held: [true],
  // preview_choice: whether the demo had loaded when they chose or left.
  loaded: [true, false],
  // signup_*: the way in. methods_shown is what the screen offered, in its order.
  method: ['apple', 'google', 'email'],
  methods_shown: ['email', 'google_email', 'apple_email', 'apple_google_email'],
  // page_viewed: the device and browser (analytics/device.ts), never the user agent itself.
  device: ['mobile', 'tablet', 'desktop'],
  os: ['ios', 'android', 'windows', 'macos', 'linux', 'other'],
  browser: ['safari', 'chrome', 'firefox', 'edge', 'samsung', 'other'],
  in_app: ['facebook', 'instagram', 'tiktok', 'none'],
  viewport: ['<380', '380-430', '430-768', '768-1024', '1024+'],
  // page_left: how far down the page they got, as a quarter (analytics/visits.ts).
  depth: ['25', '50', '75', '100'],
  // starter_chosen: which starting point, behind "Start from scratch".
  starter: ['golf', 'trip', 'potluck', 'wedding', 'other'],
} as const;
/*
 * Two props whose values are not a closed set, because they are the advertiser's own names: the
 * campaign and the ad (`Trip-W1-B`). Held to a shape instead — letters, digits and . _ - only, 64 at
 * most — so a URL with somebody's words in a utm parameter is dropped, not recorded.
 */
const PROP_PATTERNS = {
  utm_campaign: /^[A-Za-z0-9._-]{1,64}$/,
  utm_content: /^[A-Za-z0-9._-]{1,64}$/,
  // error_shown: where on screen, and the message's machine code — never its words.
  where: /^[a-z][a-z0-9_]{0,39}$/,
  code: /^[a-z][a-z0-9_]{0,39}$/,
  /*
   * offer_exit_reason's "Something else…", in their words, at most 140 characters. Sent ONLY to our
   * own log — that step goes with `pixel: false`, and no other step sets it.
   */
  text: /^[^\u0000-\u001f]{1,140}$/,
} as const;
type PropKey = keyof typeof PROP_VALUES;
export type FunnelProps = { [K in PropKey]?: (typeof PROP_VALUES)[K][number] } & { [K in keyof typeof PROP_PATTERNS]?: string };

interface FunnelEvent {
  name: FunnelStep;
  ts: string;
  props: FunnelProps;
  sessionId: string;
  visitorId: string;
}

const STEP_SET = new Set<string>(FUNNEL_STEPS);
/** Sent this long after the first step queued, so a burst of screens is one request. */
const FLUSH_AFTER_MS = 4000;
/** Never more than this held: a server that keeps failing must not grow a page's memory. */
const MAX_QUEUED = 100;

/**
 * One id per page load, random, so the export can tell one visit's steps from another's.
 * Not stored anywhere: a reload is a new one, and it says nothing about who anybody is.
 */
function newSessionId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
}
let sessionId = newSessionId();

/*
 * One id per BROWSER, random, kept in localStorage — so one person's journey reads as one line
 * across visits (the owner's journey log, 2026-10-04): the ad on Monday, the email link on Tuesday.
 * Not a fingerprint: nothing about the device goes into it, clearing site data ends it, and a
 * private window has its own. It is linked to an account only by the server, when an event
 * arrives signed in (infra/lambda/handlers/events.ts) — the first being right after the code.
 */
const VISITOR_KEY = 'saddle.visitor';
let visitorId: string | null = null;
function currentVisitorId(): string {
  if (visitorId) return visitorId;
  try {
    const kept = localStorage.getItem(VISITOR_KEY);
    if (kept && /^[A-Za-z0-9-]{8,64}$/.test(kept)) return (visitorId = kept);
    /*
     * Arrived from an app's own browser by "Open in Safari/Chrome" (platform/openInBrowser.ts):
     * the same person, so the same id — adopted only by a browser that has none of its own.
     */
    const carried = new URLSearchParams(typeof location !== 'undefined' ? location.search : '').get('vid');
    visitorId = carried && /^[A-Za-z0-9-]{8,64}$/.test(carried) ? carried : newSessionId();
    localStorage.setItem(VISITOR_KEY, visitorId);
  } catch {
    // Storage blocked: this page load's id stands in, which is what the session id already is.
    visitorId = visitorId ?? sessionId;
  }
  return visitorId;
}
let queued: FunnelEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

/** Dev and test builds send nothing, as the pixel does not — the numbers are production's. */
function enabled(): boolean {
  return process.env.NODE_ENV === 'production';
}

function clean(props: FunnelProps | undefined): FunnelProps {
  const out: Record<string, string | boolean> = {};
  if (!props) return out;
  for (const key of Object.keys(PROP_VALUES) as PropKey[]) {
    const value = (props as Record<string, unknown>)[key];
    if ((typeof value === 'string' || typeof value === 'boolean') && (PROP_VALUES[key] as readonly unknown[]).includes(value)) {
      out[key] = value;
    }
  }
  for (const key of Object.keys(PROP_PATTERNS) as (keyof typeof PROP_PATTERNS)[]) {
    const value = (props as Record<string, unknown>)[key];
    if (typeof value === 'string' && PROP_PATTERNS[key].test(value)) out[key] = value;
  }
  return out as FunnelProps;
}

/**
 * Record that somebody reached `step`. Never throws and never waits: measurement must not be
 * able to break the screen it measures. `now` sends at once, for the step taken right before
 * the page leaves (Stripe), where a timer would never fire.
 */
export function funnelStep(step: FunnelStep, props?: FunnelProps, options?: { now?: boolean; pixel?: boolean }): void {
  try {
    if (!STEP_SET.has(step) || !enabled()) return;
    // A test visit's events say so, and none of them goes to Meta (analytics/testVisit.ts).
    const test = isTestVisit();
    const kept = clean(test ? { ...props, test: true } : props);
    queued.push({ name: step, ts: new Date().toISOString(), props: kept, sessionId, visitorId: currentVisitorId() });
    if (queued.length > MAX_QUEUED) queued = queued.slice(queued.length - MAX_QUEUED);
    if (options?.now) void flushFunnel();
    else if (!timer) {
      timer = setTimeout(() => {
        timer = null;
        void flushFunnel();
      }, FLUSH_AFTER_MS);
    }
    // Meta's copy: sent only with a yes, and only in production (metaPixel.ts decides). Page views
    // and leaves are ours alone (`pixel: false`): Meta counts its own PageView.
    if (options?.pixel !== false && !test) trackPixel(step, Object.keys(kept).length ? kept : undefined);
  } catch {
    /* see above */
  }
}

/**
 * Send what is queued. Resolves whatever happens. A failed send keeps its steps for the next
 * one — a dropped connection on a phone is common, and a step lost is a drop-off that never
 * happened in the numbers.
 */
export async function flushFunnel(): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (queued.length === 0) return;
  const batch = queued;
  queued = [];
  try {
    await request('/events', { method: 'POST', body: { events: batch }, keepalive: true, retry: false, timeoutMs: 10_000 });
  } catch {
    queued = [...batch, ...queued].slice(-MAX_QUEUED);
  }
}

/*
 * The page going away — a tab closed, the app switched, the browser leaving for Stripe —
 * is the last chance to send what the timer has not yet. `keepalive` lets the request finish
 * after the page has gone.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    void flushFunnel();
  });
  // And when the page is hidden: an in-app browser may close without a pagehide at all.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushFunnel();
  });
}

/**
 * `step` once per visit to a screen: when it starts showing, not on every render. A step that
 * changes (the plan's next step) or a screen that stops and starts showing again is a new
 * visit. StrictMode runs an effect twice on mount; the ref holds across that, so it is one.
 */
export function useFunnelStep(step: FunnelStep, showing = true, props?: FunnelProps): void {
  const last = useRef<FunnelStep | null>(null);
  // Read when the step fires, not a reason to fire it again.
  const propsRef = useRef(props);
  propsRef.current = props;
  useEffect(() => {
    if (!showing) {
      last.current = null;
      return;
    }
    if (last.current === step) return;
    last.current = step;
    funnelStep(step, propsRef.current);
  }, [step, showing]);
}

/** Test-only: a fresh queue and session, with no timer left over from the last case. */
export function _resetFunnelForTest(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  queued = [];
  sessionId = newSessionId();
  // A new browser, as far as the next step can tell: read from storage afresh.
  visitorId = null;
}
